/** @type {import("./types.js").InfoPagesCatalog} */
export const infoPagesEn = {
  about: {
    id: "about",
    kicker: "About Plaatplan",
    title: "Why Plaatplan?",
    intro: "Plaatplan is built for DIY makers who plan several projects from the same sheet material in one session, with a clear cut plan for the saw.",
    sections: [
      {
        heading: "The problem",
        paragraphs: [
          "Many tools assume one project or one cut list. In practice you often build several things at once: a cabinet, a desk and something else from the same MDF sheet.",
          "You want to know quickly how much to order, how parts lay out on the sheets, and what you can hand to the hardware store or cutting service.",
        ],
      },
      {
        heading: "What Plaatplan is",
        paragraphs: [
          "A planner for multiple subprojects on one standard sheet, with optional offcuts you already have.",
          "A vendor-neutral work plan with cut list, dimensions and edge finishing, usable at any cutting service or hardware store.",
          "A tool that runs locally in your browser. No account, no cloud.",
        ],
      },
      {
        heading: "What Plaatplan is not",
        paragraphs: [
          "Not a live saw coach at the machine. Plaatplan helps you plan before buying; in the work plan you can review the cut sequence on desktop, not cut-by-cut while you saw.",
          "Not tied to one hardware store or brand. You export a generic work plan.",
          "Not an industrial optimizer. The layout is reliable and understandable, not always theoretically optimal.",
        ],
      },
      {
        heading: "Who it is for",
        paragraphs: [
          "DIY makers building cabinets, niches or interior parts from MDF, plywood or particle board, who partly have material cut or saw it themselves.",
        ],
      },
    ],
  },
  how: {
    id: "how",
    kicker: "Guide",
    title: "How it works",
    intro: "From material to work plan in four steps.",
    steps: [
      {
        title: "1. Set up your material stock",
        body: "Choose a standard sheet (size, material, grain direction) and set kerf width. Add offcuts you already have if needed.",
      },
      {
        title: "2. Enter subprojects and parts",
        body: "Create a subproject per project. Enter panels via the table or the L×W×qty name line format.",
      },
      {
        title: "3. Review the cut layout",
        body: "See how many standard sheets you need, which offcuts are used, and whether everything fits.",
      },
      {
        title: "4. Export the work plan",
        body: "Open the work plan via Export → Open work plan. Configure under Prepare, review the cut order and export as PDF.",
      },
    ],
  },
  faq: {
    id: "faq",
    kicker: "Help",
    title: "Frequently asked questions",
    intro: "Answers to common questions about Plaatplan.",
    items: [
      {
        id: "storage",
        question: "Where is my plan stored?",
        answer: "Locally in this browser (localStorage). There is no account and no cloud. Back up regularly via Export → Project file (.json). See also Import and export in the guide.",
      },
      {
        id: "takeToStore",
        question: "Can I take this to the hardware store or cutting service?",
        answer: "Yes. Use Export → Open work plan for the work plan, or What do I need for a purchase overview. See the Print work plan guide.",
      },
      {
        id: "kerf",
        question: "Does Plaatplan account for kerf (blade width)?",
        answer: "Yes. The layout is accurate to the millimetre and includes kerf. The default is 2.2 mm; adjust this under Saw settings if your blade or cutting service differs.",
      },
      {
        id: "restStock",
        question: "What is the difference between a standard sheet and offcuts?",
        answer: "A standard sheet is what you buy new (e.g. 2440 × 1220 mm). Offcuts are pieces you already have. The planner uses offcuts before new sheets.",
      },
      {
        id: "rotation",
        question: "Can panels rotate? What is grain direction?",
        answer: "With MDF and particle board you can usually rotate panels 90°. With visible plywood, lock grain direction so panels do not rotate unexpectedly.",
      },
      {
        id: "accuracy",
        question: "How accurate is the layout?",
        answer: "Plaatplan uses a fast, understandable layout, not an industrial optimizer. Always verify quantities and dimensions before ordering material.",
      },
      {
        id: "multiProject",
        question: "Can I put multiple projects in one plan?",
        answer: "Yes. Each project is a subproject. Parts with the same thickness can share a sheet, colour-coded per project.",
      },
      {
        id: "doesNotFit",
        question: "What if a part does not fit?",
        answer: "You see a warning under Results. Adjust dimensions, standard sheet or offcuts until everything fits.",
      },
      {
        id: "exportStrategies",
        question: "What are the export strategies in the work plan?",
        answer: "Efficient cutting, keep large offcut and deliver strips affect instructions and the work plan, not which panels sit where on the sheet. Turn on Show cut lines to use numbered cuts and the cut sequence preview.",
      },
      {
        id: "cutSequence",
        question: "How do I review the cut sequence?",
        answer: "In the work plan (Prepare tab) you can step through the cut sequence per sheet on desktop via View sequence. Turn on Show cut lines in saw settings. On a narrow screen you only see the static sheet diagram. See the Review cut sequence guide.",
      },
      {
        id: "demo",
        question: "How do I load the demo?",
        answer: "Click Load demo in the cut layout, or import a previously saved project file via Import in the header. More detail is in the guide.",
      },
      {
        id: "feedback",
        question: "How can I give feedback?",
        answer: "Open Feedback in the footer. Use the form to send a bug, idea or question. Only enter your email if you want a reply.",
      },
      {
        id: "support",
        question: "Can I support Plaatplan?",
        answer: "Yes, voluntarily. The footer has “Support Plaatplan”. You can contribute financially (e.g. via Tikkie), share the site, give feedback, and, when available, try the workshop app. The web planner stays free; there is no obligation.",
      },
    ],
  },
  importExport: {
    id: "importExport",
    kicker: "Guide",
    title: "Import and export projects",
    intro:
      "Your plan is saved automatically in this browser. With a project file (.json) you can back up, move work to another device, or continue later where you left off.",
    sections: [
      {
        heading: "Automatic saving",
        paragraphs: [
          "Plaatplan stores your plan locally in the browser (localStorage). You do not need to save manually. Clear site data or use another browser and that plan is gone.",
          "So back up with a project file regularly, especially before cleaning your browser or continuing on another device.",
        ],
      },
      {
        heading: "Export a project file",
        paragraphs: [
          "Click Export in the header and choose Project file. You get a .json file with your plan, subprojects, saw settings, offcuts and a snapshot of the cut layout.",
          "The file is named roughly your-plan-name-project.json. Store it on your computer, in cloud storage or email it to yourself.",
        ],
      },
      {
        heading: "Import a project",
        paragraphs: [
          "Click Import in the header and choose a previously downloaded .json file. Your current plan in this browser is replaced by the imported plan.",
          "After importing, verify dimensions, quantities and saw settings. The cut layout is recalculated from your input.",
        ],
      },
      {
        heading: "Other exports (not a backup)",
        paragraphs: [
          "Via Export you can also download a printable work plan (PDF), a cut list as CSV or a purchase overview. Those files are for the hardware store, cutting service or your own records.",
          "They do not contain everything in a project file. To edit your plan in Plaatplan later, always use the .json project file.",
        ],
      },
      {
        heading: "Tips",
        paragraphs: [
          "Before importing, back up your current plan if you still need it.",
          "A project file works on any device with a modern browser. No account is required.",
          "Do not share a project file publicly if project names or notes contain personal data.",
        ],
      },
    ],
  },
  feedback: {
    id: "feedback",
    kicker: "Contact",
    title: "Feedback",
    intro: "Missing something, something broken, or an idea? Let us know. That helps make Plaatplan better.",
  },
  privacy: {
    id: "privacy",
    kicker: "Trust",
    title: "Privacy & data",
    intro: "Plaatplan is designed to collect as little data as possible.",
    sections: [
      {
        heading: "Stored locally",
        paragraphs: [
          "Your plan is saved automatically in this browser. Nothing is sent to a Plaatplan server. There is no server-side storage and no account.",
        ],
      },
      {
        heading: "What you export",
        paragraphs: [
          "Only when you export yourself (PDF, CSV, JSON) does data leave your device. You manage those files.",
        ],
      },
      {
        heading: "Visit counting",
        paragraphs: [
          "Plaatplan does not use cookies for ads or profiling. To see if the site is useful, we count page views anonymously via GoatCounter. No personal profile is built and no cookie banner is required.",
          "More information: https://www.goatcounter.com/help/privacy.",
        ],
      },
      {
        heading: "Voluntary support",
        paragraphs: [
          "Via “Support Plaatplan” in the footer you can help voluntarily: financially (e.g. Tikkie or Mollie), by sharing the free web planner, giving feedback, or, when available, trying the workshop app. Payment happens outside Plaatplan, in a new tab.",
        ],
      },
      {
        heading: "Other browser or device",
        paragraphs: [
          "Your plan is tied to this browser on this device. Use Project file (.json) to back up or transfer.",
        ],
      },
      {
        heading: "Feedback form",
        paragraphs: [
          "When you send feedback, only your message, report type and optional email address are included. Your cut plan itself is not sent.",
        ],
      },
    ],
  },
  disclaimer: {
    id: "disclaimer",
    kicker: "Trust",
    title: "Disclaimer & use",
    intro:
      "Plaatplan is a free tool in your browser. Read briefly what you can and cannot expect.",
    sections: [
      {
        heading: "A tool, not professional advice",
        paragraphs: [
          "Plaatplan helps you plan and export. It is not structural, cutting or safety advice. Use your own judgment when buying and cutting material.",
        ],
      },
      {
        heading: "You verify dimensions and quantities",
        paragraphs: [
          "Always verify dimensions, quantities, kerf, grain direction and edge finishing before ordering or cutting. Input or interpretation errors can lead to wrong sheets or waste.",
        ],
      },
      {
        heading: "Use at your own risk",
        paragraphs: [
          "The planner and exports are provided as-is. There is no guarantee that the layout is optimal, error-free or suitable for your situation.",
        ],
      },
      {
        heading: "Limited liability",
        paragraphs: [
          "Plaatplan is not liable for material loss, damage, costs or other consequences from using the tool or exports (PDF, CSV, project file).",
        ],
      },
      {
        heading: "Intellectual property",
        paragraphs: [
          "Plaatplan software and content remain the property of the author. You may use the tool freely for your own projects. Reuse or resale of the software itself is not allowed without permission.",
        ],
      },
      {
        heading: "Feedback",
        paragraphs: [
          "What you send via the feedback form is used to improve Plaatplan. Do not include sensitive data you do not want to share.",
        ],
      },
    ],
  },
};
